import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../animations/variants';
import { useConfirm } from '../components/common/ConfirmDialog';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import JobProgressPanel from '../components/common/JobProgressPanel';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  TextField,
  Divider,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  CloudQueue as CloudIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  AccessTime as TimeIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { MESSAGES } from '../utils/constants';
import { filterInput } from '../utils/inputSanitization';

// AWS Regions list for SSO configuration
const AWS_REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-east-2', label: 'US East (Ohio)' },
  { value: 'us-west-1', label: 'US West (N. California)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'af-south-1', label: 'Africa (Cape Town)' },
  { value: 'ap-east-1', label: 'Asia Pacific (Hong Kong)' },
  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
  { value: 'ap-south-2', label: 'Asia Pacific (Hyderabad)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
  { value: 'ap-southeast-3', label: 'Asia Pacific (Jakarta)' },
  { value: 'ap-southeast-4', label: 'Asia Pacific (Melbourne)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul)' },
  { value: 'ap-northeast-3', label: 'Asia Pacific (Osaka)' },
  { value: 'ca-central-1', label: 'Canada (Central)' },
  { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
  { value: 'eu-central-2', label: 'Europe (Zurich)' },
  { value: 'eu-west-1', label: 'Europe (Ireland)' },
  { value: 'eu-west-2', label: 'Europe (London)' },
  { value: 'eu-west-3', label: 'Europe (Paris)' },
  { value: 'eu-south-1', label: 'Europe (Milan)' },
  { value: 'eu-south-2', label: 'Europe (Spain)' },
  { value: 'eu-north-1', label: 'Europe (Stockholm)' },
  { value: 'il-central-1', label: 'Israel (Tel Aviv)' },
  { value: 'me-south-1', label: 'Middle East (Bahrain)' },
  { value: 'me-central-1', label: 'Middle East (UAE)' },
  { value: 'sa-east-1', label: 'South America (São Paulo)' },
];

// Multi Step Loader Component - Minimalist Version
const MultiStepLoader = ({ steps, currentStep, isComplete }) => {
  return (
    <Box sx={{
      width: '100%',
      maxWidth: '400px',
      margin: '0 auto',
      py: 4,
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}>
        {steps.map((step, index) => {
          const isActive = index === currentStep;
          const isCompleted = index < currentStep || isComplete;
          const isPending = index > currentStep && !isComplete;

          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                px: 2,
                borderRadius: '8px',
                backgroundColor: isActive ? '#EBF5FB' : 'transparent',
                border: isActive ? '1px solid #4698D320' : '1px solid transparent',
                opacity: isPending ? 0.5 : 1,
                transition: 'all 0.3s ease',
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: isCompleted
                    ? '#27AE60'
                    : isActive
                      ? '#4698D3'
                      : '#E5E5E5',
                  fontSize: '0.875rem',
                }}
              >
                {isCompleted ? (
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                ) : (
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{index + 1}</Typography>
                )}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    color: isActive ? '#4698D3' : isCompleted ? '#27AE60' : '#666666',
                  }}
                >
                  {step.title}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.8rem',
                    color: '#999999',
                  }}
                >
                  {step.description}
                </Typography>
              </Box>

              {isActive && !isComplete && (
                <CircularProgress size={20} sx={{ color: '#4698D3' }} />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Completion indication */}
      {isComplete && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography
            sx={{
              color: '#27AE60',
              fontWeight: 600,
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <CheckCircleIcon fontSize="small" />
            Configuration loaded successfully
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Input styles for forms
const inputStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E5E5E5' },
    '&:hover fieldset': { borderColor: '#4698D3' },
    '&.Mui-focused fieldset': { borderColor: '#4698D3' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': { color: '#4698D3' },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Metropolis, sans-serif',
  },
};

const Replication = ({
  ssoConfiguration,
  fetchSsoConfiguration,
  replicateUsersToKeycloak,
  onboardAWSAccounts,
  replicateFullConfiguration,
  userReplicationResult,
  accountOnboardingResult,
  fullReplicationResult,
  ssoFetchProgress,
  replicationProgress
}) => {
  const confirm = useConfirm();
  const isReadOnly = useReadOnlyMode();
  const [isLoading, setIsLoading] = useState(false);
  const [isReplicating, setIsReplicating] = useState(false);
  const [showReplicationModal, setShowReplicationModal] = useState(false);
  const [replicationMode, setReplicationMode] = useState('actual');
  const [replicationType, setReplicationType] = useState('full');
  const [replicationStartTime, setReplicationStartTime] = useState(null);
  const [replicationDuration, setReplicationDuration] = useState(null);

  // Loading steps state
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingComplete, setLoadingComplete] = useState(false);

  // Management Account state
  const [showManagementAccountModal, setShowManagementAccountModal] = useState(false);
  const [managementAccount, setManagementAccount] = useState(null);
  const [loadingManagementAccount, setLoadingManagementAccount] = useState(false);
  const [managementAccountForm, setManagementAccountForm] = useState({
    accountId: '',
    ssoRegion: '',
    description: ''
  });
  const [isCustomerAdmin, setIsCustomerAdmin] = useState(false);

  // Define loading steps
  const loadingSteps = [
    {
      title: "Initializing Connection",
      description: "Establishing secure connection to AWS IAM Identity Center...",
    },
    {
      title: "Retrieving Users & Groups",
      description: "Loading user identities and group memberships...",
    },
    {
      title: "Processing AWS Accounts",
      description: "Loading AWS accounts and their organizations structure...",
    },
    {
      title: "Loading Permission Sets",
      description: "Processing IAM policies and permission assignments...",
    },
    {
      title: "Finalizing Configuration",
      description: "Validating data and preparing for display...",
    }
  ];

  // Check customer context and fetch management account on mount
  useEffect(() => {
    const adminDetails = localStorage.getItem('adminDetails');
    if (adminDetails) {
      const parsed = JSON.parse(adminDetails);
      setIsCustomerAdmin(!!parsed.customerId);
    }
    fetchManagementAccount();
  }, []);

  // Simulate loading steps progression
  useEffect(() => {
    let interval;

    if (isLoading && loadingStep < loadingSteps.length - 1) {
      const stepTimes = [2500, 3500, 3000, 4000];

      interval = setTimeout(() => {
        setLoadingStep(prev => {
          if (prev < loadingSteps.length - 1) {
            return prev + 1;
          }
          return prev;
        });
      }, stepTimes[loadingStep] || 3000);
    }

    return () => {
      if (interval) clearTimeout(interval);
    };
  }, [isLoading, loadingStep, loadingSteps.length]);

  // Management Account functions
  const fetchManagementAccount = async () => {
    setLoadingManagementAccount(true);
    try {
      const response = await apiService.getManagementAccount();
      if (response.success) {
        setManagementAccount(response.data);
      } else {
        setManagementAccount(null);
      }
    } catch (err) {
      const errorMessage = err?.message?.toLowerCase() || '';
      const isNotFoundError =
        errorMessage.includes('404') ||
        errorMessage.includes('not found') ||
        errorMessage.includes('not configured') ||
        err?.response?.status === 404;

      if (!isNotFoundError) {
        console.error('Failed to fetch management account:', err);
        toast.error('Failed to fetch management account configuration');
      }
      setManagementAccount(null);
    } finally {
      setLoadingManagementAccount(false);
    }
  };

  const handleManagementAccountSubmit = async (e) => {
    e.preventDefault();
    setLoadingManagementAccount(true);

    try {
      const response = await apiService.initializeManagementAccount(managementAccountForm);
      if (response.success) {
        toast.success(MESSAGES.MANAGEMENT_ACCOUNT_INITIALIZED);
        setManagementAccountForm({ accountId: '', ssoRegion: '', description: '' });
        await fetchManagementAccount();
        setShowManagementAccountModal(false);
      }
    } catch (err) {
      toast.error(MESSAGES.INIT_MANAGEMENT_ACCOUNT_ERROR);
    } finally {
      setLoadingManagementAccount(false);
    }
  };

  const handleManagementAccountDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete Configuration',
      message: 'Are you sure you want to delete the management account configuration?',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) {
      return;
    }

    setLoadingManagementAccount(true);

    try {
      const response = await apiService.deleteManagementAccount();
      if (response.success) {
        toast.success(MESSAGES.MANAGEMENT_ACCOUNT_DELETED);
        setManagementAccount(null);
      }
    } catch (err) {
      toast.error(MESSAGES.DELETE_MANAGEMENT_ACCOUNT_ERROR);
    } finally {
      setLoadingManagementAccount(false);
    }
  };

  const validateAccountId = (accountId) => {
    return /^\d{12}$/.test(accountId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const handleFetchConfiguration = async () => {
    // Check if management account is configured
    if (!managementAccount) {
      toast.warning('Management account is not configured. Please configure the management account first.');
      return;
    }

    setIsLoading(true);
    setLoadingStep(0);
    setLoadingComplete(false);

    try {
      await fetchSsoConfiguration();
      setLoadingStep(loadingSteps.length - 1);

      setTimeout(() => {
        setLoadingComplete(true);

        setTimeout(() => {
          setIsLoading(false);
        }, 1500);
      }, 1000);
    } catch (error) {
      console.error('Error fetching SSO configuration:', error);
      // Show generic message for 500 errors, otherwise show API message
      const is500Error = error?.response?.status === 500;
      const errorMessage = is500Error
        ? 'Failed to fetch SSO configuration'
        : (error?.response?.data?.message || error?.message || 'Failed to fetch SSO configuration');
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  const handleReplication = async (dryRun = true) => {
    const startTime = Date.now();
    setReplicationStartTime(startTime);
    setReplicationDuration(null);
    setIsReplicating(true);
    // Dismiss the confirmation as soon as the job is launched. Closing it after
    // the await instead kept it covering the live progress table for the whole
    // run — and left it stuck open permanently if replication threw.
    setShowReplicationModal(false);

    try {
      switch (replicationType) {
        case 'users':
          await replicateUsersToKeycloak(dryRun);
          break;
        case 'accounts':
          await onboardAWSAccounts(dryRun);
          break;
        case 'full':
        default:
          await replicateFullConfiguration(dryRun);
          break;
      }
    } catch (error) {
      console.error('Error during replication:', error);
    } finally {
      const endTime = Date.now();
      const duration = endTime - startTime;
      setReplicationDuration(duration);
      setIsReplicating(false);
    }
  };

  const formatDuration = (milliseconds) => {
    if (!milliseconds) return 'N/A';

    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const hasValidData = ssoConfiguration &&
    ((ssoConfiguration.users && ssoConfiguration.users.length > 0) ||
     (ssoConfiguration.groups && ssoConfiguration.groups.length > 0));

  // Stats cards configuration
  const statsCards = ssoConfiguration ? [
    { title: 'AWS Accounts', count: ssoConfiguration.accounts?.length || 0, icon: <CloudIcon sx={{ fontSize: 24 }} />, color: '#F39C12' },
    { title: 'Total Users', count: ssoConfiguration.users?.length || 0, icon: <PeopleIcon sx={{ fontSize: 24 }} />, color: '#4698D3' },
    { title: 'Total Groups', count: ssoConfiguration.groups?.length || 0, icon: <GroupIcon sx={{ fontSize: 24 }} />, color: '#D64794' },
    { title: 'Permission Sets', count: ssoConfiguration.permission_sets?.length || 0, icon: <SecurityIcon sx={{ fontSize: 24 }} />, color: '#27AE60' },
    { title: 'User Assignments', count: ssoConfiguration.user_assignments?.length || 0, icon: <AssignmentIcon sx={{ fontSize: 24 }} />, color: '#9B59B6' },
  ] : [];

  // AWS lists disabled users in the directory, but replication never acts on
  // them beyond soft-disabling: they are skipped before group assignment and
  // never get memberships resolved. In a SCIM-fed tenant they can outnumber the
  // active accounts, so listing them buries the ones that matter — the preview
  // shows active users and reports the disabled tally as a number instead.
  const activeSsoUsers = ssoConfiguration?.users?.filter(u => u.status !== 'DISABLED') || [];
  const disabledSsoUserCount = (ssoConfiguration?.users?.length || 0) - activeSsoUsers.length;

  // These lists are a preview of what replication will write, not a browsable
  // directory. Mounting a row per record froze the renderer outright at 10k
  // users + 8k groups, so only the head of each list is rendered and the
  // truncation is stated rather than left implicit.
  const PREVIEW_LIMIT = 100;
  const previewOf = (list) => (list || []).slice(0, PREVIEW_LIMIT);
  const truncationNote = (list) =>
    (list || []).length > PREVIEW_LIMIT
      ? `Showing first ${PREVIEW_LIMIT.toLocaleString()} of ${list.length.toLocaleString()}`
      : null;

  const previewNoteSx = {
    fontFamily: 'Metropolis, sans-serif',
    fontSize: '0.75rem',
    color: '#8A94A6',
    textAlign: 'center',
    pt: 1.5,
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Page Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#4698D3',
                width: 48,
                height: 48,
              }}
            >
              <SyncIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                SSO Configuration Replication
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                  fontWeight: 500,
                }}
              >
                Fetch and replicate AWS IAM Identity Center configuration
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              disabled={isReadOnly}
              onClick={() => {
                if (managementAccount) {
                  setManagementAccountForm({
                    accountId: managementAccount.accountId,
                    ssoRegion: managementAccount.ssoRegion || '',
                    description: managementAccount.description || ''
                  });
                }
                setShowManagementAccountModal(true);
              }}
              sx={{
                borderColor: managementAccount ? '#27AE60' : '#E5E5E5',
                color: managementAccount ? '#27AE60' : '#666666',
                backgroundColor: managementAccount ? '#F0F9F4' : '#FFFFFF',
                borderRadius: '6px',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderColor: managementAccount ? '#219A52' : '#4698D3',
                  backgroundColor: managementAccount ? '#E8F8F0' : '#F9FBFD',
                },
              }}
            >
              {managementAccount ? 'Configured' : 'Configure'}
            </Button>

            <Button
              variant="outlined"
              startIcon={
                isLoading ? (
                  <CircularProgress size={16} sx={{ color: '#4698D3' }} />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={handleFetchConfiguration}
              disabled={isLoading}
              sx={{
                color: '#666666',
                backgroundColor: '#F9FBFD',
                borderColor: '#E5E5E5',
                borderRadius: '6px',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: '#F0F4F8',
                  borderColor: '#4698D3',
                  color: '#4698D3',
                },
                '&:disabled': {
                  color: '#BDBDBD',
                  backgroundColor: '#F9FBFD',
                },
              }}
            >
              Fetch SSO Configuration
            </Button>

            {hasValidData && (
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={() => setShowReplicationModal(true)}
                disabled={isReadOnly || isReplicating}
                sx={{
                  backgroundColor: '#4698D3',
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#3580B8',
                  },
                }}
              >
                {isReplicating ? 'Replicating...' : 'Start Replication'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Live counters for a replication run, shown above whatever else the
          page is displaying so progress stays visible while it works. */}
      {replicationProgress && (
        <motion.div variants={sectionVariants}>
          <JobProgressPanel
            title={isReplicating ? 'Replication in progress' : 'Last replication run'}
            subtitle="Resources written to Keycloak and AWS"
            progress={replicationProgress}
          />
        </motion.div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <motion.div variants={sectionVariants}>
        {/* Real per-resource counts once the first poll lands; the stepped
            loader only covers the couple of seconds before that. */}
        {ssoFetchProgress ? (
          <JobProgressPanel
            title="Fetching SSO configuration"
            subtitle="Reading AWS IAM Identity Center — large directories take a few minutes"
            progress={ssoFetchProgress}
          />
        ) : (
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              borderRadius: '8px',
              p: 4,
              minHeight: '400px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <MultiStepLoader
              steps={loadingSteps}
              currentStep={loadingStep}
              isComplete={loadingComplete}
            />
          </Paper>
        )}
        </motion.div>
      ) : ssoConfiguration ? (
        <>
          {/* Configuration Overview - Stats Cards */}
          <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              borderRadius: '8px',
              p: 3,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                fontSize: '1.1rem',
                color: '#253E66',
                mb: 3,
              }}
            >
              Configuration Overview
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {statsCards.map((stat, index) => (
                <Box key={stat.title} sx={{ flex: 1, minWidth: 0 }}>
                  <Card
                    sx={{
                      height: '100%',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E5E5',
                      borderRadius: '8px',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        borderColor: stat.color,
                        boxShadow: `0 4px 12px ${stat.color}20`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            backgroundColor: `${stat.color}12`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: stat.color,
                          }}
                        >
                          {stat.icon}
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 700,
                            fontSize: '2rem',
                            color: stat.color,
                            lineHeight: 1,
                          }}
                        >
                          {stat.count}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontSize: '0.8rem',
                          color: '#666666',
                          fontWeight: 600,
                        }}
                      >
                        {stat.title}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Paper>
          </motion.div>

          {/* Replication Results */}
          {(userReplicationResult || accountOnboardingResult || fullReplicationResult) && (
            <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #27AE6030',
                borderRadius: '8px',
                p: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <CheckCircleIcon sx={{ color: '#27AE60' }} />
                <Typography
                  sx={{
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#253E66',
                  }}
                >
                  Last Replication Result
                </Typography>
                {replicationDuration && (
                  <Chip
                    icon={<TimeIcon />}
                    label={`Duration: ${formatDuration(replicationDuration)}`}
                    size="small"
                    sx={{
                      backgroundColor: '#F0F9F4',
                      color: '#27AE60',
                      border: '1px solid #27AE6020',
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>

              {/* Results Statistics */}
              {userReplicationResult && (
                <Grid container spacing={2}>
                  {[
                    { label: 'Users', success: userReplicationResult.successful_user_sync || 0, failed: userReplicationResult.failed_user_sync || 0, skipped: userReplicationResult.skipped_users || 0, total: userReplicationResult.total_users || 0 },
                    { label: 'Groups', success: userReplicationResult.successful_group_sync || 0, failed: userReplicationResult.failed_group_sync || 0, skipped: userReplicationResult.skipped_groups || 0, total: userReplicationResult.total_groups || 0 },
                    { label: 'Permission Sets', success: userReplicationResult.successful_permission_sync || 0, failed: userReplicationResult.failed_permission_sync || 0, skipped: userReplicationResult.skipped_permission_sets || 0, total: userReplicationResult.total_permission_sets || 0 },
                    { label: 'Assignments', success: userReplicationResult.successful_assignment_sync || 0, failed: userReplicationResult.failed_assignment_sync || 0, skipped: userReplicationResult.skipped_assignments || 0, total: userReplicationResult.total_assignments || 0 },
                  ].map((stat) => (
                    <Grid item xs={12} sm={6} md={3} key={stat.label}>
                      <Card
                        sx={{
                          backgroundColor: '#F9FBFD',
                          border: '1px solid #E5E5E5',
                          borderRadius: '8px',
                          p: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            color: '#253E66',
                            mb: 1.5
                          }}
                        >
                          {stat.label}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Chip
                            label={`✓ ${stat.success}`}
                            size="small"
                            sx={{ backgroundColor: '#F0F9F4', color: '#27AE60', fontWeight: 600 }}
                          />
                          {stat.failed > 0 && (
                            <Chip
                              label={`✗ ${stat.failed}`}
                              size="small"
                              sx={{ backgroundColor: '#FDF2F2', color: '#E74C3C', fontWeight: 600 }}
                            />
                          )}
                          {stat.skipped > 0 && (
                            <Chip
                              label={`⊗ ${stat.skipped}`}
                              size="small"
                              sx={{ backgroundColor: '#FEF9E7', color: '#F39C12', fontWeight: 600 }}
                            />
                          )}
                          {stat.total > 0 && (
                            <Chip
                              label={`Total: ${stat.total}`}
                              size="small"
                              sx={{ backgroundColor: '#F9FBFD', color: '#666666', fontWeight: 500 }}
                            />
                          )}
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Error Lists */}
              {((userReplicationResult?.user_errors && userReplicationResult.user_errors.length > 0) ||
                (userReplicationResult?.errors && userReplicationResult.errors.length > 0)) && (
                <Alert
                  severity="error"
                  sx={{
                    mt: 2,
                    backgroundColor: '#FDF2F2',
                    border: '1px solid #E74C3C20',
                    '& .MuiAlert-icon': { color: '#E74C3C' },
                  }}
                >
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.875rem', mb: 1, color: '#253E66' }}>
                    User Errors ({(userReplicationResult.user_errors || userReplicationResult.errors || []).length})
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {(userReplicationResult.user_errors || userReplicationResult.errors || []).slice(0, 3).map((error, index) => (
                      <ListItem key={index} sx={{
                        '@keyframes fadeInItem': {
                          from: { opacity: 0, transform: 'translateY(8px)' },
                          to: { opacity: 1, transform: 'translateY(0)' },
                        },
                        animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                        py: 0,
                      }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <ErrorIcon sx={{ color: '#E74C3C', fontSize: '1rem' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${error.user?.username || error.user?.display_name}: ${error.error}`}
                          primaryTypographyProps={{
                            sx: { fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#253E66' }
                          }}
                        />
                      </ListItem>
                    ))}
                    {(userReplicationResult.user_errors || userReplicationResult.errors || []).length > 3 && (
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#666666', pl: 3.5 }}>
                        ... and {(userReplicationResult.user_errors || userReplicationResult.errors || []).length - 3} more errors
                      </Typography>
                    )}
                  </List>
                </Alert>
              )}
            </Paper>
            </motion.div>
          )}

          {/* AWS Accounts Section */}
          <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              borderRadius: '8px',
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ backgroundColor: '#F39C12', width: 40, height: 40 }}>
                <CloudIcon />
              </Avatar>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#253E66',
                  }}
                >
                  AWS Accounts ({ssoConfiguration.accounts?.length || 0})
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.8rem',
                    color: '#999999',
                    fontWeight: 500,
                  }}
                >
                  Accounts discovered from AWS SSO/Organizations
                </Typography>
              </Box>
            </Box>

            {ssoConfiguration.accounts?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {ssoConfiguration.accounts.map((account, index) => (
                  <Box
                    key={account.account_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#F9FBFD',
                      border: '1px solid #E5E5E5',
                      borderRadius: '6px',
                      px: 2.5,
                      py: 1.5,
                      '@keyframes fadeInItem': {
                        from: { opacity: 0, transform: 'translateY(6px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      // Capped: the stagger is per-item, so an uncapped delay on a
                      // 10k-row list holds the tail at opacity 0 (fill-mode both)
                      // for several minutes, making most of the list invisible.
                      animation: `fadeInItem 0.3s ease-out ${Math.min(index, 25) * 0.04}s both`,
                      transition: 'border-color 0.15s ease',
                      '&:hover': { borderColor: '#F39C12' },
                    }}
                  >
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66' }}>
                      {account.name}
                    </Typography>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#999999' }}>
                      {account.account_id}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CloudIcon sx={{ fontSize: 48, color: '#E5E5E5', mb: 2 }} />
                <Typography sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
                  No AWS accounts found in SSO configuration
                </Typography>
              </Box>
            )}
          </Paper>
          </motion.div>

          {/* Users Section */}
          <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              borderRadius: '8px',
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ backgroundColor: '#4698D3', width: 40, height: 40 }}>
                <PeopleIcon />
              </Avatar>
              <Typography
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  color: '#253E66',
                }}
              >
                Users ({activeSsoUsers.length.toLocaleString()})
              </Typography>
              {disabledSsoUserCount > 0 && (
                <Chip
                  label={`${disabledSsoUserCount.toLocaleString()} disabled, not listed`}
                  size="small"
                  sx={{
                    backgroundColor: '#F1F3F6',
                    color: '#666666',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.7rem',
                  }}
                />
              )}
            </Box>

            {activeSsoUsers.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {previewOf(activeSsoUsers).map((user, index) => (
                  <Box
                    key={user.user_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#F9FBFD',
                      border: '1px solid #E5E5E5',
                      borderRadius: '6px',
                      px: 2.5,
                      py: 1.5,
                      '@keyframes fadeInItem': {
                        from: { opacity: 0, transform: 'translateY(6px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      // Capped: the stagger is per-item, so an uncapped delay on a
                      // 10k-row list holds the tail at opacity 0 (fill-mode both)
                      // for several minutes, making most of the list invisible.
                      animation: `fadeInItem 0.3s ease-out ${Math.min(index, 25) * 0.04}s both`,
                      transition: 'border-color 0.15s ease',
                      '&:hover': { borderColor: '#4698D3' },
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66' }}>
                        {user.display_name}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#666666' }}>
                        {user.email || 'No email'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999999' }}>
                        {user.username || 'N/A'}
                      </Typography>
                      {/* Group memberships are resolved during replication, not during
                          this fetch, so the chip only appears once they are known —
                          rendering "0 groups" here would state something untrue. */}
                      {user.group_memberships?.length > 0 && (
                        <Chip label={`${user.group_memberships.length} groups`} size="small" sx={{ mt: 0.5, backgroundColor: '#EBF5FB', color: '#4698D3', fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                  </Box>
                ))}
                {truncationNote(activeSsoUsers) && (
                  <Typography sx={previewNoteSx}>{truncationNote(activeSsoUsers)}</Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PeopleIcon sx={{ fontSize: 48, color: '#E5E5E5', mb: 2 }} />
                <Typography sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
                  {/* Distinguish an empty directory from one where every user is
                      disabled — "No users found" would be wrong in the latter. */}
                  {disabledSsoUserCount > 0
                    ? `No active users — all ${disabledSsoUserCount.toLocaleString()} users are disabled in AWS`
                    : 'No users found'}
                </Typography>
              </Box>
            )}
          </Paper>
          </motion.div>

          {/* Groups Section */}
          <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              borderRadius: '8px',
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ backgroundColor: '#D64794', width: 40, height: 40 }}>
                <GroupIcon />
              </Avatar>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#253E66',
                  }}
                >
                  Groups ({ssoConfiguration.groups?.length || 0})
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.8rem',
                    color: '#999999',
                    fontWeight: 500,
                  }}
                >
                  Groups from AWS SSO/Identity Center
                </Typography>
              </Box>
            </Box>

            {ssoConfiguration.groups?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {previewOf(ssoConfiguration.groups).map((group, index) => (
                  <Box
                    key={group.group_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#F9FBFD',
                      border: '1px solid #E5E5E5',
                      borderRadius: '6px',
                      px: 2.5,
                      py: 1.5,
                      '@keyframes fadeInItem': {
                        from: { opacity: 0, transform: 'translateY(6px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      // Capped: the stagger is per-item, so an uncapped delay on a
                      // 10k-row list holds the tail at opacity 0 (fill-mode both)
                      // for several minutes, making most of the list invisible.
                      animation: `fadeInItem 0.3s ease-out ${Math.min(index, 25) * 0.04}s both`,
                      transition: 'border-color 0.15s ease',
                      '&:hover': { borderColor: '#D64794' },
                    }}
                  >
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66' }}>
                      {group.name}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#999999', maxWidth: '50%', textAlign: 'right' }}>
                      {group.description || 'No description'}
                    </Typography>
                  </Box>
                ))}
                {truncationNote(ssoConfiguration.groups) && (
                  <Typography sx={previewNoteSx}>{truncationNote(ssoConfiguration.groups)}</Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <GroupIcon sx={{ fontSize: 48, color: '#E5E5E5', mb: 2 }} />
                <Typography sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
                  No groups found
                </Typography>
              </Box>
            )}
          </Paper>
          </motion.div>

          {/* Permission Sets Section */}
          <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              borderRadius: '8px',
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ backgroundColor: '#27AE60', width: 40, height: 40 }}>
                <SecurityIcon />
              </Avatar>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#253E66',
                  }}
                >
                  Permission Sets ({ssoConfiguration.permission_sets?.length || 0})
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.8rem',
                    color: '#999999',
                    fontWeight: 500,
                  }}
                >
                  AWS SSO permission sets and policies
                </Typography>
              </Box>
            </Box>

            {ssoConfiguration.permission_sets?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {previewOf(ssoConfiguration.permission_sets).map((permSet, index) => {
                  const policyCount = (permSet.managedPolicies?.length || 0) + (permSet.inlinePolicies ? Object.keys(permSet.inlinePolicies).length : 0);
                  return (
                    <Box
                      key={permSet.arn}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#F9FBFD',
                        border: '1px solid #E5E5E5',
                        borderRadius: '6px',
                        px: 2.5,
                        py: 1.5,
                        '@keyframes fadeInItem': {
                          from: { opacity: 0, transform: 'translateY(6px)' },
                          to: { opacity: 1, transform: 'translateY(0)' },
                        },
                        // Capped: the stagger is per-item, so an uncapped delay on a
                      // 10k-row list holds the tail at opacity 0 (fill-mode both)
                      // for several minutes, making most of the list invisible.
                      animation: `fadeInItem 0.3s ease-out ${Math.min(index, 25) * 0.04}s both`,
                        transition: 'border-color 0.15s ease',
                        '&:hover': { borderColor: '#27AE60' },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66' }}>
                          {permSet.name}
                        </Typography>
                        {permSet.description && (
                          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#999999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {permSet.description}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
                        {permSet.managedPolicies?.length > 0 && (
                          <Chip label={`${permSet.managedPolicies.length} Managed`} size="small" sx={{ backgroundColor: '#F0F9F4', color: '#27AE60', fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.7rem', border: '1px solid #27AE6020' }} />
                        )}
                        {permSet.inlinePolicies && Object.keys(permSet.inlinePolicies).length > 0 && (
                          <Chip label={`${Object.keys(permSet.inlinePolicies).length} Inline`} size="small" sx={{ backgroundColor: '#EBF5FB', color: '#4698D3', fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.7rem', border: '1px solid #4698D320' }} />
                        )}
                        {policyCount === 0 && (
                          <Chip label="No Policies" size="small" sx={{ backgroundColor: '#F9FBFD', color: '#999999', fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.7rem', border: '1px solid #E5E5E5' }} />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <SecurityIcon sx={{ fontSize: 48, color: '#E5E5E5', mb: 2 }} />
                <Typography sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
                  No permission sets found
                </Typography>
              </Box>
            )}
          </Paper>
          </motion.div>

          {/* User Assignments Section */}
          <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              borderRadius: '8px',
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ backgroundColor: '#9B59B6', width: 40, height: 40 }}>
                <AssignmentIcon />
              </Avatar>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#253E66',
                  }}
                >
                  User Assignments ({ssoConfiguration.user_assignments?.length || 0})
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.8rem',
                    color: '#999999',
                    fontWeight: 500,
                  }}
                >
                  User and group assignments to AWS accounts
                </Typography>
              </Box>
            </Box>

            {ssoConfiguration.user_assignments?.length > 0 ? (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E5E5',
                  borderRadius: '8px',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', borderBottom: '1px solid #E5E5E5' }}>
                        Account
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', borderBottom: '1px solid #E5E5E5' }}>
                        Permission Set
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', borderBottom: '1px solid #E5E5E5' }}>
                        Principal Type
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', borderBottom: '1px solid #E5E5E5' }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewOf(ssoConfiguration.user_assignments).map((assignment, index) => (
                      <TableRow
                        key={`${assignment.account_id}-${assignment.permission_set_name}-${assignment.principal_id}-${index}`}
                        sx={{
                          '@keyframes fadeInRow': {
                            from: { opacity: 0, transform: 'translateY(8px)' },
                            to: { opacity: 1, transform: 'translateY(0)' },
                          },
                          animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                          '&:hover': {
                            backgroundColor: '#F9FBFD',
                          },
                        }}
                      >
                        <TableCell sx={{ borderBottom: '1px solid #E5E5E5', py: 2 }}>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: 'Metropolis, sans-serif',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                color: '#253E66',
                              }}
                            >
                              {assignment.account_name}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                color: '#999999',
                              }}
                            >
                              {assignment.account_id}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666666', borderBottom: '1px solid #E5E5E5' }}>
                          {assignment.permission_set_name}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                          <Chip
                            label={assignment.principal_type}
                            size="small"
                            sx={{
                              backgroundColor: assignment.principal_type === 'USER' ? '#EBF5FB' : '#F8E8F2',
                              color: assignment.principal_type === 'USER' ? '#4698D3' : '#D64794',
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                          {assignment.error ? (
                            <Box>
                              <Chip
                                icon={<ErrorIcon />}
                                label="Error"
                                size="small"
                                sx={{
                                  backgroundColor: '#FDF2F2',
                                  color: '#E74C3C',
                                  border: '1px solid #E74C3C20',
                                  fontFamily: 'Metropolis, sans-serif',
                                  fontWeight: 500,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontFamily: 'Metropolis, sans-serif',
                                  fontSize: '0.7rem',
                                  color: '#E74C3C',
                                  mt: 0.5,
                                }}
                              >
                                {assignment.error}
                              </Typography>
                            </Box>
                          ) : (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Active"
                              size="small"
                              sx={{
                                backgroundColor: '#F0F9F4',
                                color: '#27AE60',
                                border: '1px solid #27AE6020',
                                fontFamily: 'Metropolis, sans-serif',
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AssignmentIcon sx={{ fontSize: 48, color: '#E5E5E5', mb: 2 }} />
                <Typography sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
                  No user assignments found
                </Typography>
              </Box>
            )}
          </Paper>
          </motion.div>
        </>
      ) : (
        /* Empty State */
        <motion.div variants={sectionVariants}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            p: 6,
            textAlign: 'center',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              width: 64,
              height: 64,
              backgroundColor: '#EBF5FB',
            }}
          >
            <SyncIcon sx={{ fontSize: 32, color: '#4698D3' }} />
          </Avatar>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1.1rem',
              color: '#253E66',
            }}
          >
            No SSO Configuration Loaded
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.875rem',
              color: '#666666',
              maxWidth: 400,
            }}
          >
            Click "Fetch SSO Configuration" above to fetch the current AWS IAM Identity Center configuration.
          </Typography>
        </Paper>
        </motion.div>
      )}

      {/* Replication Modal */}
      <Dialog
        open={showReplicationModal}
        onClose={() => setShowReplicationModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: '1px solid #E5E5E5',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#EBF5FB',
            borderBottom: '1px solid #E5E5E5',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            color: '#253E66',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
            px: 3,
            position: 'relative',
          }}
        >
          <Avatar sx={{ backgroundColor: '#4698D3', width: 40, height: 40 }}>
            <SyncIcon />
          </Avatar>
          Replicate to Prism
          <IconButton
            onClick={() => setShowReplicationModal(false)}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666666',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              '&:hover': { backgroundColor: '#F9FBFD' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, backgroundColor: '#FFFFFF' }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.95rem',
                color: '#253E66',
                lineHeight: 1.6,
              }}
            >
              This will replicate all users, groups, and AWS accounts to Prism. Do you want to continue?
            </Typography>
          </Box>
          </motion.div>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #E5E5E5',
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setShowReplicationModal(false)}
            disabled={isReplicating}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              color: '#666666',
              borderColor: '#E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#999999',
                backgroundColor: '#F9FBFD',
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleReplication(replicationMode === 'dryRun')}
            disabled={isReadOnly || isReplicating}
            startIcon={isReplicating ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <PlayArrowIcon />}
            sx={{
              backgroundColor: '#4698D3',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              px: 3,
              '&:hover': {
                backgroundColor: '#3580B8',
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E5E5',
                color: '#999999',
              },
            }}
          >
            {isReplicating ? 'Processing...' : 'Start Replication'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Management Account Configuration Modal */}
      <Dialog
        open={showManagementAccountModal}
        onClose={() => {
          setShowManagementAccountModal(false);
          setManagementAccountForm({ accountId: '', ssoRegion: '', description: '' });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: '1px solid #E5E5E5',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#EBF5FB',
            borderBottom: '1px solid #E5E5E5',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            color: '#253E66',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
            px: 3,
            position: 'relative',
          }}
        >
          <Avatar sx={{ backgroundColor: '#4698D3', width: 40, height: 40 }}>
            <CloudIcon />
          </Avatar>
          {managementAccount ? 'Management Account Configuration' : 'Configure Management Account'}
          <IconButton
            onClick={() => {
              setShowManagementAccountModal(false);
              setManagementAccountForm({ accountId: '', ssoRegion: '', description: '' });
            }}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666666',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              '&:hover': { backgroundColor: '#F9FBFD' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2, pt: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          {/* Current Configuration Display */}
          {managementAccount && (
            <Box
              sx={{
                backgroundColor: '#F9FBFD',
                borderRadius: '6px',
                p: 2,
                mb: 3,
                border: '1px solid #E5E5E5',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    color: '#253E66',
                  }}
                >
                  Current Configuration
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : "Delete Configuration"}>
                    <span>
                    <IconButton
                      onClick={handleManagementAccountDelete}
                      disabled={isReadOnly || loadingManagementAccount}
                      size="small"
                      sx={{
                        color: '#E74C3C',
                        backgroundColor: '#FDF2F2',
                        border: '1px solid #E74C3C30',
                        borderRadius: '6px',
                        '&:hover': { backgroundColor: '#FCE4E4' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>

              <Grid container spacing={2}>
                {[
                  { label: 'Account ID', value: managementAccount.accountId, monospace: true },
                  { label: 'SSO Region', value: `${AWS_REGIONS.find(r => r.value === managementAccount.ssoRegion)?.label || managementAccount.ssoRegion} (${managementAccount.ssoRegion})` },
                  { label: 'Role Name', value: managementAccount.roleName },
                  { label: 'Status', value: managementAccount.status, chip: true },
                  { label: 'Last Used', value: formatDate(managementAccount.lastUsed) },
                ].map((item) => (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontSize: '0.75rem',
                          color: '#999999',
                          fontWeight: 500,
                          mb: 0.5,
                        }}
                      >
                        {item.label}
                      </Typography>
                      {item.chip ? (
                        <Chip
                          label={item.value}
                          size="small"
                          sx={{
                            backgroundColor: '#E8F8F0',
                            color: '#27AE60',
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 500,
                          }}
                        />
                      ) : (
                        <Typography
                          sx={{
                            fontFamily: item.monospace ? 'monospace' : 'Metropolis, sans-serif',
                            fontSize: '0.875rem',
                            color: '#253E66',
                            fontWeight: 500,
                          }}
                        >
                          {item.value}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {managementAccount.description && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.75rem',
                      color: '#999999',
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    Description
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.875rem',
                      color: '#253E66',
                    }}
                  >
                    {managementAccount.description}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Configuration Form */}
          <Divider sx={{ mb: 3, borderColor: '#E5E5E5' }} />

          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '0.9rem',
              color: '#253E66',
              mb: 2,
            }}
          >
            {managementAccount ? 'Update Configuration' : 'New Configuration'}
          </Typography>

          <Box component="form" onSubmit={handleManagementAccountSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="AWS Account ID"
              value={managementAccountForm.accountId}
              onChange={(e) => setManagementAccountForm({ ...managementAccountForm, accountId: filterInput(e.target.value, 'NUMERIC') })}
              placeholder="123456789012"
              required
              size="small"
              error={managementAccountForm.accountId && !validateAccountId(managementAccountForm.accountId)}
              helperText={managementAccountForm.accountId && !validateAccountId(managementAccountForm.accountId) ? 'Account ID must be exactly 12 digits' : 'Enter the 12-digit AWS account ID'}
              sx={inputStyle}
            />

            <FormControl
              size="small"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Metropolis, sans-serif',
                  backgroundColor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E5E5' },
                  '&:hover fieldset': { borderColor: '#4698D3' },
                  '&.Mui-focused fieldset': { borderColor: '#4698D3' },
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Metropolis, sans-serif',
                  color: '#666666',
                  '&.Mui-focused': { color: '#4698D3' },
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: 'Metropolis, sans-serif',
                },
              }}
            >
              <InputLabel id="sso-region-label">SSO Region</InputLabel>
              <Select
                labelId="sso-region-label"
                value={managementAccountForm.ssoRegion}
                onChange={(e) => setManagementAccountForm({ ...managementAccountForm, ssoRegion: e.target.value })}
                label="SSO Region"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      '& .MuiMenuItem-root': {
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.875rem',
                      },
                    },
                  },
                }}
              >
                {AWS_REGIONS.map((region) => (
                  <MenuItem key={region.value} value={region.value}>
                    {region.label} ({region.value})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Description (Optional)"
              value={managementAccountForm.description}
              onChange={(e) => setManagementAccountForm({ ...managementAccountForm, description: filterInput(e.target.value, 'GENERAL_TEXT') })}
              placeholder="AWS Organization Management Account"
              multiline
              rows={2}
              size="small"
              sx={inputStyle}
            />
          </Box>
          </motion.div>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #E5E5E5',
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => {
              setShowManagementAccountModal(false);
              setManagementAccountForm({ accountId: '', ssoRegion: '', description: '' });
            }}
            disabled={loadingManagementAccount}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              color: '#666666',
              borderColor: '#E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#999999',
                backgroundColor: '#F9FBFD',
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleManagementAccountSubmit}
            disabled={isReadOnly || loadingManagementAccount || !validateAccountId(managementAccountForm.accountId) || !managementAccountForm.ssoRegion}
            startIcon={loadingManagementAccount ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <CloudIcon />}
            sx={{
              backgroundColor: '#4698D3',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              px: 3,
              '&:hover': {
                backgroundColor: '#3580B8',
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E5E5',
                color: '#999999',
              },
            }}
          >
            {loadingManagementAccount ? 'Saving...' : (managementAccount ? 'Update' : 'Configure')}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default Replication;
